import { slugify, slugifyCity, STATES, stateByCode, getStateName } from "../constants.js";
import * as XLSX from "xlsx";

export const STATE_NAMES = STATES.reduce((acc, s) => {
  acc[s.code] = s.name;
  return acc;
}, {});

/**
 * Universal file parser supporting:
 * - Excel spreadsheets (.xlsx, .xls, .ods)
 * - JSON files (.json)
 * - Tab-separated files (.tsv)
 * - Standard CSV files (.csv, .txt)
 */
export async function parseUniversalFile(file) {
  if (!file) return [];
  const filename = (file.name || "").toLowerCase();

  // 1. JSON file
  if (filename.endsWith(".json")) {
    const text = await file.text();
    try {
      const data = JSON.parse(text);
      if (Array.isArray(data)) return data;
      if (data && typeof data === "object") {
        for (const key of Object.keys(data)) {
          if (Array.isArray(data[key])) return data[key];
        }
        return [data];
      }
    } catch (e) {
      throw new Error(`Invalid JSON format: ${e.message}`);
    }
    return [];
  }

  // 2. Excel spreadsheets (.xlsx, .xls, .ods)
  if (
    filename.endsWith(".xlsx") ||
    filename.endsWith(".xls") ||
    filename.endsWith(".ods")
  ) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        throw new Error("Excel workbook contains no sheets");
      }
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
        raw: false,
      });
      return rows;
    } catch (e) {
      throw new Error(`Failed to parse Excel spreadsheet: ${e.message}`);
    }
  }

  // 3. Delimited text (CSV, TSV, TXT)
  const text = await file.text();
  return parseCsv(text);
}

/**
 * Robust RFC 4180 CSV/TSV parser
 * Handles quotes, commas/tabs/semicolons, escaped quotes (""), newlines within fields, and BOM markers.
 */
export function parseCsv(text, explicitDelimiter = null) {
  if (!text || typeof text !== "string") return [];

  // Remove BOM if present
  let cleanText = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  // Auto-detect delimiter if not specified
  let delimiter = explicitDelimiter;
  if (!delimiter) {
    const firstLine = cleanText.split(/\r\n|\n|\r/)[0] || "";
    if (firstLine.includes("\t") && !firstLine.includes(",")) {
      delimiter = "\t";
    } else if (firstLine.includes(";") && !firstLine.includes(",")) {
      delimiter = ";";
    } else {
      delimiter = ",";
    }
  }

  const rows = [];
  let currentRow = [];
  let currentField = "";
  let insideQuotes = false;
  let i = 0;
  const len = cleanText.length;

  while (i < len) {
    const char = cleanText[i];
    const nextChar = i + 1 < len ? cleanText[i + 1] : "";

    if (insideQuotes) {
      if (char === '"') {
        if (nextChar === '"') {
          // Escaped quote
          currentField += '"';
          i += 2;
          continue;
        } else {
          // Closing quote
          insideQuotes = false;
          i++;
          continue;
        }
      } else {
        currentField += char;
        i++;
        continue;
      }
    } else {
      if (char === '"') {
        insideQuotes = true;
        i++;
        continue;
      } else if (char === delimiter) {
        currentRow.push(currentField);
        currentField = "";
        i++;
        continue;
      } else if (char === "\r" && nextChar === "\n") {
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = "";
        i += 2;
        continue;
      } else if (char === "\n" || char === "\r") {
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = "";
        i++;
        continue;
      } else {
        currentField += char;
        i++;
        continue;
      }
    }
  }

  // Push last field & row if anything remains
  if (currentField !== "" || currentRow.length > 0) {
    currentRow.push(currentField);
    rows.push(currentRow);
  }

  // Filter out completely empty rows
  const nonEmptyRows = rows.filter((r) => r.some((field) => field && field.trim() !== ""));
  if (nonEmptyRows.length === 0) return [];

  // First row is headers
  const rawHeaders = nonEmptyRows[0].map((h) => (h || "").trim());
  const dataRows = nonEmptyRows.slice(1);

  return dataRows.map((row) => {
    const obj = {};
    rawHeaders.forEach((header, idx) => {
      obj[header] = row[idx] !== undefined ? row[idx].trim() : "";
    });
    return obj;
  });
}

/**
 * Generate CSV string from rows and columns
 */
export function toCsv(data, columns) {
  if (!data || !Array.isArray(data) || data.length === 0) {
    if (columns && Array.isArray(columns)) {
      return columns.map((c) => escapeCsvField(typeof c === "string" ? c : c.key || c.label)).join(",") + "\n";
    }
    return "";
  }

  const colKeys = columns
    ? columns.map((c) => (typeof c === "string" ? c : c.key))
    : Object.keys(data[0]);

  const colHeaders = columns
    ? columns.map((c) => (typeof c === "string" ? c : c.label || c.key))
    : colKeys;

  const headerLine = colHeaders.map(escapeCsvField).join(",");
  const dataLines = data.map((item) => {
    return colKeys
      .map((k) => {
        let val = item[k];
        if (val === undefined || val === null) val = "";
        else if (typeof val === "object") val = JSON.stringify(val);
        else val = String(val);
        return escapeCsvField(val);
      })
      .join(",");
  });

  return [headerLine, ...dataLines].join("\n");
}

function escapeCsvField(val) {
  if (val === null || val === undefined) return '""';
  const str = String(val);
  if (str.includes('"') || str.includes(",") || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Trigger download of CSV in browser
 */
export function downloadCsv(filename, csvContent) {
  const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download styled Excel (.xlsx) workbook in browser
 */
export function downloadExcel(filename, rows, columns, sheetName = "Sheet1") {
  if (!rows || rows.length === 0) return;

  const colKeys = columns
    ? columns.map((c) => (typeof c === "string" ? c : c.key))
    : Object.keys(rows[0]);

  const colHeaders = columns
    ? columns.map((c) => (typeof c === "string" ? c : c.label || c.key))
    : colKeys;

  const aoa = [
    colHeaders,
    ...rows.map((r) =>
      colKeys.map((k) => {
        const val = r[k];
        if (val === undefined || val === null) return "";
        if (typeof val === "object") return JSON.stringify(val);
        return String(val);
      })
    ),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  // Column auto-sizing
  worksheet["!cols"] = colHeaders.map((header, idx) => {
    let maxLen = String(header).length;
    rows.forEach((r) => {
      const val = r[colKeys[idx]];
      if (val !== undefined && val !== null) {
        maxLen = Math.max(maxLen, String(val).length);
      }
    });
    return { wch: Math.min(Math.max(maxLen + 3, 14), 45) };
  });

  const workbook = XLSX.utils.book_new();
  const safeSheetName = (sheetName || "Data").replace(/[\\/?*[\]]/g, "").slice(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName || "Data");

  const finalName = filename.endsWith(".xlsx") ? filename : `${filename.replace(/\.csv$/, "")}.xlsx`;
  XLSX.writeFile(workbook, finalName);
}

/**
 * Download sample Excel (.xlsx) template for a section
 */
export function downloadSampleExcel(sectionId) {
  const section = CSV_SECTIONS[sectionId];
  if (!section) return;
  const filename = (section.filename || `${sectionId}_template.csv`).replace(/\.csv$/, ".xlsx");
  downloadExcel(filename, section.sampleRows, section.columns, section.badge || "Template");
}

/**
 * Download sample CSV (.csv) template for a section
 */
export function downloadSampleCsv(sectionId) {
  const section = CSV_SECTIONS[sectionId];
  if (!section) return;
  const csvStr = toCsv(section.sampleRows, section.columns);
  downloadCsv(section.filename, csvStr);
}

/**
 * Export section data to Excel (.xlsx)
 */
export function exportSectionToExcel(sectionId, items) {
  const section = CSV_SECTIONS[sectionId];
  if (!section) return;
  const filename = `${sectionId}_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
  downloadExcel(filename, items, section.columns, section.badge || "Export");
}

/**
 * Export section data to CSV (.csv)
 */
export function exportSectionToCsv(sectionId, items) {
  const section = CSV_SECTIONS[sectionId];
  if (!section) return;
  const filename = `${sectionId}_export_${new Date().toISOString().slice(0, 10)}.csv`;
  const csvStr = toCsv(items, section.columns);
  downloadCsv(filename, csvStr);
}

/**
 * Header alias mapper for fuzzy column matching
 */
export function mapRowHeaders(row, aliasMap) {
  const mapped = {};
  const lowerRowKeys = {};
  Object.keys(row).forEach((k) => {
    lowerRowKeys[k.toLowerCase().replace(/[^a-z0-9]/g, "")] = row[k];
  });

  Object.keys(aliasMap).forEach((targetKey) => {
    const possibleNames = [targetKey, ...(aliasMap[targetKey] || [])];
    for (const name of possibleNames) {
      const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, "");
      if (lowerRowKeys[normalized] !== undefined && lowerRowKeys[normalized] !== "") {
        mapped[targetKey] = lowerRowKeys[normalized];
        break;
      }
    }
  });

  return mapped;
}

// -------------------------------------------------------------
// SECTION DEFINITIONS & TEMPLATES
// -------------------------------------------------------------

export const CSV_SECTIONS = {
  courses: {
    id: "courses",
    title: "Golf Courses Directory",
    badge: "Courses",
    description: "Import golf courses with venue details, location, ratings, pricing, and operating hours.",
    filename: "minigolf_courses_template.csv",
    columns: [
      { key: "name", label: "Business Name", required: true, description: "Official venue title" },
      { key: "city", label: "City", required: true, description: "City location" },
      { key: "state", label: "State", required: true, description: "State name (e.g. Maine) or 2-letter code (e.g. ME)" },
      { key: "address", label: "Address", required: false, description: "Street address" },
      { key: "phone", label: "Phone Number", required: false, description: "Contact phone number" },
      { key: "website", label: "Website URL", required: false, description: "Official URL" },
      { key: "priceRange", label: "Price Range", required: false, description: "e.g. $10 - $16 per player" },
      { key: "rating", label: "Rating", required: false, description: "Rating score (0.0 - 5.0)" },
      { key: "reviewCount", label: "Reviews", required: false, description: "Total reviews count" },
      { key: "image", label: "Image URL", required: false, description: "Cover photo link" },
      { key: "featured", label: "Featured", required: false, description: "true or false" },
      { key: "description", label: "Description", required: false, description: "Venue overview and course highlights" },
      { key: "seoTitle", label: "SEO Title", required: false, description: "Custom meta title tag" },
      { key: "seoDescription", label: "SEO Description", required: false, description: "Custom meta description" },
      { key: "hours_monday", label: "Hours Mon", required: false, description: "e.g. 10:00 AM - 9:00 PM" },
      { key: "hours_tuesday", label: "Hours Tue", required: false, description: "Operating hours" },
      { key: "hours_wednesday", label: "Hours Wed", required: false, description: "Operating hours" },
      { key: "hours_thursday", label: "Hours Thu", required: false, description: "Operating hours" },
      { key: "hours_friday", label: "Hours Fri", required: false, description: "Operating hours" },
      { key: "hours_saturday", label: "Hours Sat", required: false, description: "Operating hours" },
      { key: "hours_sunday", label: "Hours Sun", required: false, description: "Operating hours" },
    ],
    aliases: {
      name: ["businessname", "business", "coursename", "course", "title", "venuename", "name", "listingname", "facilityname"],
      city: ["city", "town", "municipality", "locationcity"],
      state: ["state", "statecode", "st", "province", "region"],
      address: ["address", "street", "streetaddress", "location", "fulladdress"],
      phone: ["phonenumber", "phone", "tel", "telephone", "cell", "mobile", "contactphone"],
      website: ["websiteurl", "website", "url", "web", "link", "weburl", "siteurl"],
      priceRange: ["pricerange", "price", "pricing", "cost", "rates"],
      rating: ["rating", "score", "stars", "overallrating"],
      reviewCount: ["reviews", "reviewcount", "numreviews", "totalreviews", "userreviews"],
      featured: ["featured", "isfeatured", "highlighted", "promoted"],
      description: ["description", "about", "desc", "details", "overview"],
      image: ["imageurl", "image", "photo", "img", "photourl", "picture", "pictureurl", "thumbnail"],
      seoTitle: ["seotitle", "metatitle"],
      seoDescription: ["seodescription", "metadescription"],
      hours_monday: ["hoursmonday", "monhours", "monday", "hoursmon"],
      hours_tuesday: ["hourstuesday", "tuehours", "tuesday", "hourstue"],
      hours_wednesday: ["hourswednesday", "wedhours", "wednesday", "hourswed"],
      hours_thursday: ["hoursthursday", "thuhours", "thursday", "hoursthu"],
      hours_friday: ["hoursfriday", "frihours", "friday", "hoursfri"],
      hours_saturday: ["hourssaturday", "sathours", "saturday", "hourssat"],
      hours_sunday: ["hourssunday", "sunhours", "sunday", "hourssun"],
    },
    sampleRows: [
      {
        name: "Moosehead Mini Golf",
        city: "Shirley Mills",
        state: "Maine",
        address: "118 Greenville Rd, Shirley Mills, ME 04485, USA",
        phone: "(207) 695-3414",
        website: "https://www.facebook.com/mooseheadminigolf",
        priceRange: "$10 - $14 per round",
        rating: "4.9",
        reviewCount: "9",
        featured: "true",
        description: "Charming family-friendly 18-hole miniature golf course in the heart of Shirley Mills.",
        image: "https://ik.imagekit.io/minigolf/images/placeholder.jpg",
        seoTitle: "Moosehead Mini Golf | Shirley Mills ME",
        seoDescription: "Play mini golf at Moosehead Mini Golf in Shirley Mills, Maine.",
        hours_monday: "10:00 AM - 9:00 PM",
        hours_tuesday: "10:00 AM - 9:00 PM",
        hours_wednesday: "10:00 AM - 9:00 PM",
        hours_thursday: "10:00 AM - 9:00 PM",
        hours_friday: "10:00 AM - 10:00 PM",
        hours_saturday: "10:00 AM - 10:00 PM",
        hours_sunday: "10:00 AM - 8:00 PM",
      },
      {
        name: "Dolphin Mini Golf",
        city: "Boothbay",
        state: "Maine",
        address: "510 Wiscasset Rd, Boothbay, ME 04537, USA",
        phone: "(207) 633-4828",
        website: "https://www.facebook.com/dolphinminigolf",
        priceRange: "$11 - $15 per round",
        rating: "4.6",
        reviewCount: "224",
        featured: "true",
        description: "Classic coastal Maine miniature golf with ice cream bar and nautical obstacles.",
        image: "https://ik.imagekit.io/minigolf/images/placeholder.jpg",
        seoTitle: "Dolphin Mini Golf | Boothbay ME",
        seoDescription: "Nautical-themed mini golf in Boothbay, ME with arcade games and treats.",
        hours_monday: "10:00 AM - 10:00 PM",
        hours_tuesday: "10:00 AM - 10:00 PM",
        hours_wednesday: "10:00 AM - 10:00 PM",
        hours_thursday: "10:00 AM - 10:00 PM",
        hours_friday: "10:00 AM - 11:00 PM",
        hours_saturday: "10:00 AM - 11:00 PM",
        hours_sunday: "10:00 AM - 9:00 PM",
      },
      {
        name: "Pirates Island Adventure Golf",
        city: "Gulf Shores",
        state: "AL",
        address: "3201 Gulf Shores Pkwy, Gulf Shores, AL 36542",
        phone: "(251) 968-4653",
        website: "https://piratesislandgolf.com",
        priceRange: "$12 - $16 per round",
        rating: "4.7",
        reviewCount: "820",
        featured: "true",
        description: "Explore 18 lush tropical pirate-themed mini golf holes with waterfalls and caves.",
        image: "https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=800",
        seoTitle: "Pirates Island Adventure Golf | Gulf Shores AL",
        seoDescription: "Play mini golf in Gulf Shores at Pirates Island with 18 tropical adventure holes.",
        hours_monday: "10:00 AM - 10:00 PM",
        hours_tuesday: "10:00 AM - 10:00 PM",
        hours_wednesday: "10:00 AM - 10:00 PM",
        hours_thursday: "10:00 AM - 10:00 PM",
        hours_friday: "10:00 AM - 11:00 PM",
        hours_saturday: "10:00 AM - 11:00 PM",
        hours_sunday: "10:00 AM - 10:00 PM",
      },
    ],
    normalize(raw) {
      let state = (raw.state || raw.stateCode || raw.st || "").trim().toLowerCase();
      // Resolve full state name if provided (e.g. "Maine" -> "me")
      if (state.length > 2) {
        const foundEntry = Object.entries(STATE_NAMES).find(
          ([code, name]) => name.toLowerCase() === state || code.toLowerCase() === state
        );
        if (foundEntry) state = foundEntry[0];
      }

      // Fallback: extract 2-letter state code from address if state was missing/invalid
      if (!state || !STATE_NAMES[state]) {
        const addrMatch = (raw.address || "").match(/\b([A-Za-z]{2})\s+\d{5}\b/);
        if (addrMatch && STATE_NAMES[addrMatch[1].toLowerCase()]) {
          state = addrMatch[1].toLowerCase();
        }
      }

      // If still missing or invalid, default to al
      if (!state || !STATE_NAMES[state]) {
        state = "al";
      }

      let city = (raw.city || raw.town || raw.municipality || raw.location || "").trim();
      // Fallback: try to deduce city from address (e.g., "118 Greenville Rd, Shirley Mills, ME 04485")
      if (!city && raw.address) {
        const parts = raw.address.split(",").map((p) => p.trim());
        if (parts.length >= 3) {
          city = parts[1];
        } else if (parts.length === 2) {
          city = parts[0];
        }
      }

      if (!city) {
        city = "General";
      }

      const defaultHours = {
        Monday: "10:00 AM - 10:00 PM",
        Tuesday: "10:00 AM - 10:00 PM",
        Wednesday: "10:00 AM - 10:00 PM",
        Thursday: "10:00 AM - 10:00 PM",
        Friday: "10:00 AM - 11:00 PM",
        Saturday: "10:00 AM - 11:00 PM",
        Sunday: "11:00 AM - 9:00 PM",
      };

      const hours = {
        Monday: raw.hours_monday || raw.hours_mon || raw.monday || defaultHours.Monday,
        Tuesday: raw.hours_tuesday || raw.hours_tue || raw.tuesday || defaultHours.Tuesday,
        Wednesday: raw.hours_wednesday || raw.hours_wed || raw.wednesday || defaultHours.Wednesday,
        Thursday: raw.hours_thursday || raw.hours_thu || raw.thursday || defaultHours.Thursday,
        Friday: raw.hours_friday || raw.hours_fri || raw.friday || defaultHours.Friday,
        Saturday: raw.hours_saturday || raw.hours_sat || raw.saturday || defaultHours.Saturday,
        Sunday: raw.hours_sunday || raw.hours_sun || raw.sunday || defaultHours.Sunday,
      };

      const parsedRating = parseFloat(raw.rating);
      const parsedReviews = parseInt(raw.reviewCount || raw.reviews, 10);

      // Resilient name extraction from any available column
      const courseName = (
        raw.name ||
        raw.businessName ||
        raw.courseName ||
        raw.title ||
        raw.facilityName ||
        raw.venue ||
        raw.place ||
        raw.listingName ||
        ""
      ).trim();

      const defaultDesc = courseName
        ? `Experience a fun round of miniature golf at ${courseName}${city ? ` in ${city}` : ""}. Great course obstacles and entertainment for family and friends.`
        : "Experience a fun miniature golf round for family and friends.";

      return {
        name: courseName,
        city: city,
        state: state,
        address: raw.address || "",
        phone: raw.phone || "",
        website: raw.website || "",
        priceRange: raw.priceRange || "$10 - $15 per round",
        rating: !isNaN(parsedRating) ? parsedRating : 4.5,
        reviewCount: !isNaN(parsedReviews) ? parsedReviews : 0,
        featured: raw.featured === true || String(raw.featured).toLowerCase() === "true" || String(raw.featured) === "1",
        description: raw.description || defaultDesc,
        image: raw.image || "",
        seoTitle: raw.seoTitle || (courseName ? `${courseName} | ${city || "Mini Golf"}` : ""),
        seoDescription: raw.seoDescription || (courseName ? `Play mini golf at ${courseName}${city ? ` in ${city}` : ""}. Check hours, pricing, and ratings.` : ""),
        hours,
      };
    },
    validate(item) {
      // Lenient validation: never drop rows, only warn if course has absolutely no identifier
      const errors = [];
      if (!item.name || !item.name.trim()) {
        errors.push("Course Name was empty (will auto-name during import)");
      }
      return errors;
    },
  },

  states: {
    id: "states",
    title: "State Content Pages",
    badge: "States",
    description: "Import state page customized titles, SEO lead text, and introduction paragraphs.",
    filename: "state_pages_content_template.csv",
    columns: [
      { key: "stateCode", label: "State Code", required: true, description: "2-letter state code e.g. AL, FL, CA" },
      { key: "stateName", label: "State Name", required: false, description: "Full name e.g. Alabama" },
      { key: "customTitle", label: "Custom Title", required: false, description: "Headline shown on state page" },
      { key: "leadParagraph", label: "Lead Paragraph", required: false, description: "Introductory bold highlight text" },
      { key: "contentParagraphs", label: "Content Paragraphs", required: false, description: "Main text paragraphs separated by | pipe symbol" },
    ],
    aliases: {
      stateCode: ["statecode", "state", "code", "st"],
      stateName: ["statename", "name"],
      customTitle: ["customtitle", "title", "headline"],
      leadParagraph: ["leadparagraph", "lead", "intro", "summary"],
      contentParagraphs: ["contentparagraphs", "paragraphs", "content", "body"],
    },
    sampleRows: [
      {
        stateCode: "al",
        stateName: "Alabama",
        customTitle: "The Premier Guide to Mini Golf Courses in Alabama",
        leadParagraph: "Discover premier miniature golf spots across Alabama, from Gulf Shores coastal adventures to Birmingham glowing courses.",
        contentParagraphs: "Alabama is home to some of the most charming outdoor and indoor miniature golf courses in the South.|Whether you are looking for family-friendly obstacle greens or glow-in-the-dark date nights, find your next round here.",
      },
      {
        stateCode: "fl",
        stateName: "Florida",
        customTitle: "Top-Rated Miniature Golf Greens Across Florida",
        leadParagraph: "Experience world-famous pirate themes, tropical lagoons, and championship mini golf in the Sunshine State.",
        contentParagraphs: "Florida boasts the highest concentration of elaborately themed miniature golf venues in the country.|From Orlando resort courses to Panama City Beach classics, enjoy year-round putting fun under the sun.",
      },
    ],
    normalize(raw) {
      let code = (raw.stateCode || raw.state || "").trim().toLowerCase();
      if (code.length > 2) {
        const found = Object.entries(STATE_NAMES).find(
          ([c, name]) => name.toLowerCase() === code || c === code
        );
        if (found) code = found[0];
      }
      const stateName = raw.stateName || STATE_NAMES[code] || code.toUpperCase();
      let paragraphs = [];
      if (raw.contentParagraphs) {
        paragraphs = String(raw.contentParagraphs)
          .split(/\||\n\n+/)
          .map((p) => p.trim())
          .filter(Boolean);
      }

      return {
        stateCode: code,
        stateName,
        customTitle: raw.customTitle || "",
        leadParagraph: raw.leadParagraph || "",
        contentParagraphs: paragraphs,
      };
    },
    validate(item) {
      const errors = [];
      if (!item.stateCode || !item.stateCode.trim()) errors.push("State Code is required");
      return errors;
    },
  },

  cities: {
    id: "cities",
    title: "City Content Pages",
    badge: "Cities",
    description: "Import city landing page custom headlines, lead paragraphs, and content descriptions.",
    filename: "city_pages_content_template.csv",
    columns: [
      { key: "state", label: "State Code", required: true, description: "2-letter state code e.g. AL" },
      { key: "cityName", label: "City Name", required: true, description: "City title e.g. Gulf Shores" },
      { key: "citySlug", label: "City Slug", required: false, description: "URL slug (auto-generated if blank)" },
      { key: "customTitle", label: "Custom Title", required: false, description: "Heading for this city's guide" },
      { key: "leadParagraph", label: "Lead Paragraph", required: false, description: "Introductory lead statement" },
      { key: "contentParagraphs", label: "Content Paragraphs", required: false, description: "Main guide paragraphs separated by | pipe symbol" },
    ],
    aliases: {
      state: ["state", "statecode", "st"],
      cityName: ["cityname", "city", "town"],
      citySlug: ["cityslug", "slug"],
      customTitle: ["customtitle", "title", "heading"],
      leadParagraph: ["leadparagraph", "lead", "intro"],
      contentParagraphs: ["contentparagraphs", "paragraphs", "content", "body"],
    },
    sampleRows: [
      {
        state: "al",
        cityName: "Gulf Shores",
        citySlug: "gulf-shores",
        customTitle: "Best Mini Golf Courses in Gulf Shores, Alabama",
        leadParagraph: "Gulf Shores offers some of the best coastal pirate-themed and water-feature mini golf courses in the United States.",
        contentParagraphs: "Families visiting the Alabama Gulf Coast will find award-winning 18-hole putting courses featuring waterfalls, caves, and pirate ships.|Most courses are conveniently located along Gulf Shores Parkway with arcade games and ice cream nearby.",
      },
      {
        state: "al",
        cityName: "Birmingham",
        citySlug: "birmingham",
        customTitle: "Top Mini Golf & Indoor Glow Courses in Birmingham",
        leadParagraph: "Explore premier indoor and outdoor putting courses in Birmingham for birthday celebrations and weekend fun.",
        contentParagraphs: "From glow-in-the-dark neon blacklight venues to sprawling outdoor family entertainment parks, Birmingham offers exciting mini golf for all skill levels.",
      },
    ],
    normalize(raw) {
      let state = (raw.state || "").trim().toLowerCase();
      if (state.length > 2) {
        const found = Object.entries(STATE_NAMES).find(
          ([c, name]) => name.toLowerCase() === state || c === state
        );
        if (found) state = found[0];
      }
      const cityName = (raw.cityName || raw.city || "").trim();
      const citySlug = (raw.citySlug || slugifyCity(cityName)).trim().toLowerCase();

      let paragraphs = [];
      if (raw.contentParagraphs) {
        paragraphs = String(raw.contentParagraphs)
          .split(/\||\n\n+/)
          .map((p) => p.trim())
          .filter(Boolean);
      }

      return {
        state: state || "al",
        cityName,
        citySlug,
        customTitle: raw.customTitle || "",
        leadParagraph: raw.leadParagraph || "",
        contentParagraphs: paragraphs,
      };
    },
    validate(item) {
      const errors = [];
      if (!item.state || !item.state.trim()) errors.push("State code is required");
      if (!item.cityName || !item.cityName.trim()) errors.push("City name is required");
      return errors;
    },
  },

  blogs: {
    id: "blogs",
    title: "Blog Articles & Guides",
    badge: "Blog",
    description: "Import blog articles, buying guides, course spotlight stories, and mini golf tips.",
    filename: "blog_articles_template.csv",
    columns: [
      { key: "title", label: "Article Title", required: true, description: "Headline of the post" },
      { key: "slug", label: "Slug", required: false, description: "URL slug (auto-generated if empty)" },
      { key: "category", label: "Category", required: false, description: "e.g. Guides & Tips, Course Spotlights, Family Fun" },
      { key: "author", label: "Author", required: false, description: "Writer name or editorial team" },
      { key: "readTime", label: "Read Time", required: false, description: "e.g. 5 min read" },
      { key: "coverImage", label: "Cover Image URL", required: false, description: "Direct image link" },
      { key: "excerpt", label: "Excerpt", required: false, description: "Short summary shown on cards" },
      { key: "content", label: "Content", required: true, description: "Full article body (Markdown supported)" },
      { key: "published", label: "Published", required: false, description: "true or false" },
      { key: "publishedAt", label: "Publish Date", required: false, description: "e.g. September 13, 2026" },
    ],
    aliases: {
      title: ["title", "headline", "posttitle"],
      slug: ["slug", "urlslug"],
      category: ["category", "topic", "tag"],
      author: ["author", "writer", "by"],
      readTime: ["readtime", "readingtime", "read_time"],
      coverImage: ["coverimage", "image", "imageurl", "featuredimage"],
      excerpt: ["excerpt", "summary", "shortdesc", "snippet"],
      content: ["content", "body", "article", "text"],
      published: ["published", "ispublished", "status"],
      publishedAt: ["publishedat", "date", "publishdate", "createdat"],
    },
    sampleRows: [
      {
        title: "10 Pro Tips to Lower Your Mini Golf Score",
        slug: "10-pro-tips-to-lower-your-mini-golf-score",
        category: "Guides & Tips",
        author: "Putting Editor",
        readTime: "4 min read",
        coverImage: "https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=1200",
        excerpt: "Master bank shots, pacing, and reading green contours to crush your friends on the next round.",
        content: "Miniature golf may seem like pure luck, but physics and geometry are on your side when you understand these key strategies:\n\n1. Use The Rails Wisely\nBank shots behave just like billiard pool tables.\n\n2. Pace Over Power\nGentle strokes give you a much higher margin of error around the cup.",
        published: "true",
        publishedAt: "September 12, 2026",
      },
      {
        title: "The Ultimate Guide to Coastal Mini Golf in Alabama",
        slug: "ultimate-guide-coastal-mini-golf-alabama",
        category: "Course Spotlights",
        author: "Travel Desk",
        readTime: "6 min read",
        coverImage: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1200",
        excerpt: "From Gulf Shores pirate bays to Orange Beach adventure trails, discover the best coastal putting greens.",
        content: "Alabama's coast offers more than white sands; it's a world-class hotspot for miniature golf enthusiasts of every age.",
        published: "true",
        publishedAt: "September 10, 2026",
      },
    ],
    normalize(raw) {
      const title = (raw.title || "").trim();
      const slug = (raw.slug || slugify(title)).trim();
      return {
        title,
        slug,
        category: raw.category || "Guides & Tips",
        author: raw.author || "Admin",
        readTime: raw.readTime || "5 min read",
        coverImage: raw.coverImage || "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1200",
        excerpt: raw.excerpt || (raw.content ? raw.content.slice(0, 150) + "..." : ""),
        content: raw.content || "",
        published: raw.published === undefined || raw.published === true || String(raw.published).toLowerCase() === "true",
        publishedAt: raw.publishedAt || new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
      };
    },
    validate(item) {
      const errors = [];
      if (!item.title || !item.title.trim()) errors.push("Article Title is required");
      if (!item.content || !item.content.trim()) errors.push("Article Content is required");
      return errors;
    },
  },

  messages: {
    id: "messages",
    title: "Inquiries & Contact Messages",
    badge: "Inquiries",
    description: "Import historical contact inquiries, player messages, or venue listing update requests.",
    filename: "contact_messages_template.csv",
    columns: [
      { key: "name", label: "Sender Name", required: true, description: "Full name of inquirer" },
      { key: "email", label: "Email", required: true, description: "Contact email address" },
      { key: "subject", label: "Subject", required: true, description: "Message topic line" },
      { key: "category", label: "Category", required: false, description: "General Inquiry, Course Correction, Partnership, etc." },
      { key: "message", label: "Message Content", required: true, description: "Body of the inquiry" },
      { key: "isRead", label: "Is Read", required: false, description: "true or false" },
      { key: "createdAt", label: "Date Received", required: false, description: "ISO timestamp or human date" },
    ],
    aliases: {
      name: ["name", "sender", "fullname", "contactname"],
      email: ["email", "emailaddress", "mail"],
      subject: ["subject", "topic", "title"],
      category: ["category", "type", "reason"],
      message: ["message", "content", "inquiry", "body", "text"],
      isRead: ["isread", "read", "status"],
      createdAt: ["createdat", "date", "time", "timestamp"],
    },
    sampleRows: [
      {
        name: "Sarah Jenkins",
        email: "sarah@example.com",
        subject: "Listing hours update for Gulf Shores venue",
        category: "Course Correction",
        message: "Hi! The summer hours for Pirates Island have extended to 11 PM on weeknights. Thank you!",
        isRead: "true",
        createdAt: "2026-09-10T14:32:00Z",
      },
      {
        name: "Marcus Vance",
        email: "marcus@funputt.com",
        subject: "Add our new glow course in Mobile",
        category: "Add Course Request",
        message: "We just launched an indoor 18-hole glow golf course in Mobile, AL and would love to be listed in your directory.",
        isRead: "false",
        createdAt: "2026-09-12T09:15:00Z",
      },
    ],
    normalize(raw) {
      return {
        name: (raw.name || "").trim(),
        email: (raw.email || "").trim(),
        subject: (raw.subject || "General Inquiry").trim(),
        category: raw.category || "General Inquiry",
        message: (raw.message || "").trim(),
        isRead: String(raw.isRead).toLowerCase() === "true",
        createdAt: raw.createdAt || new Date().toISOString(),
      };
    },
    validate(item) {
      const errors = [];
      if (!item.name || !item.name.trim()) errors.push("Sender Name is required");
      if (!item.email || !item.email.trim()) errors.push("Email is required");
      if (!item.message || !item.message.trim()) errors.push("Message body is required");
      return errors;
    },
  },

  content: {
    id: "content",
    title: "Homepage & Site Content",
    badge: "Content",
    description: "Import site homepage text, hero titles, spotlight banners, and stats via key-value CSV.",
    filename: "homepage_content_template.csv",
    columns: [
      { key: "field", label: "Content Field Key", required: true, description: "e.g. heroTitle1, heroSubtitle, spotlightTitle, footerTagline" },
      { key: "value", label: "Value / Text", required: true, description: "Content string" },
    ],
    aliases: {
      field: ["field", "key", "contentkey", "name", "property"],
      value: ["value", "text", "content", "val"],
    },
    sampleRows: [
      { field: "heroTitle1", value: "Find Mini Golf Courses" },
      { field: "heroTitle2", value: "Near You in Alabama & Nationwide" },
      { field: "heroSubtitle", value: "Your ultimate directory for 4,000+ top-rated indoor and outdoor miniature golf courses across all 50 states." },
      { field: "spotlightTitle", value: "Featured Spotlight: Mini Golf in Alabama" },
      { field: "spotlightSubtitle", value: "Explore popular indoor and outdoor putt putt courses from coastal greens to glowing blacklight lounges." },
      { field: "topRatedTitle", value: "Top Rated Mini Golf Courses" },
      { field: "statesDirectoryTitle", value: "Browse All Cities by State" },
      { field: "footerTagline", value: "Your #1 guide to finding the best indoor and outdoor mini golf putt putt courses in the United States." },
      { field: "copyrightText", value: "Mini Golf Alabama (minigolfalabama.com). All rights reserved." },
    ],
    normalize(raw) {
      return {
        field: (raw.field || raw.key || "").trim(),
        value: (raw.value || raw.text || "").trim(),
      };
    },
    validate(item) {
      const errors = [];
      if (!item.field) errors.push("Field key is required");
      return errors;
    },
  },

  policies: {
    id: "policies",
    title: "Legal & Policies",
    badge: "Policies",
    description: "Import sections for Privacy Policy and Terms & Conditions.",
    filename: "legal_policies_sections_template.csv",
    columns: [
      { key: "policyType", label: "Policy Type", required: true, description: "privacy or terms" },
      { key: "sectionTitle", label: "Section Title", required: true, description: "e.g. 1. Information We Collect" },
      { key: "sectionContent", label: "Section Content", required: true, description: "Detailed legal text" },
    ],
    aliases: {
      policyType: ["policytype", "policy", "type"],
      sectionTitle: ["sectiontitle", "title", "heading"],
      sectionContent: ["sectioncontent", "content", "text", "body"],
    },
    sampleRows: [
      {
        policyType: "privacy",
        sectionTitle: "1. Information We Collect",
        sectionContent: "When you visit our directory, our servers automatically record standard browser data, such as your IP address, browser type, referring URLs, and pages viewed.",
      },
      {
        policyType: "privacy",
        sectionTitle: "2. Cookies and Storage",
        sectionContent: "We use essential session storage and local cache to store user interface preferences and cache directory listings for instant page loading.",
      },
      {
        policyType: "terms",
        sectionTitle: "1. Directory Information Disclaimer",
        sectionContent: "While we strive to keep all course information, hours of operation, pricing, and ratings accurate, details can change without notice.",
      },
      {
        policyType: "terms",
        sectionTitle: "2. Permitted Use",
        sectionContent: "You are granted a non-exclusive, revocable license to access and browse our directory for personal, non-commercial purposes.",
      },
    ],
    normalize(raw) {
      const type = (raw.policyType || "privacy").trim().toLowerCase();
      return {
        policyType: type.includes("term") ? "terms" : "privacy",
        sectionTitle: (raw.sectionTitle || "").trim(),
        sectionContent: (raw.sectionContent || "").trim(),
      };
    },
    validate(item) {
      const errors = [];
      if (!item.sectionTitle) errors.push("Section Title is required");
      if (!item.sectionContent) errors.push("Section Content is required");
      return errors;
    },
  },

  about: {
    id: "about",
    title: "About Us Page Elements",
    badge: "About",
    description: "Import key statistics, core values, or mission paragraphs for the About Us page.",
    filename: "about_content_template.csv",
    columns: [
      { key: "type", label: "Type", required: true, description: "stat, value, or story" },
      { key: "title", label: "Title / Number / Heading", required: true, description: "e.g. 4,000+ for stat or Verified & Accurate for value" },
      { key: "description", label: "Description / Label", required: true, description: "e.g. Courses Listed or value description" },
      { key: "extra", label: "Extra / Subtitle / Icon", required: false, description: "Icon name e.g. Trophy, Compass, or stat subtitle" },
    ],
    aliases: {
      type: ["type", "section", "category"],
      title: ["title", "number", "heading", "label"],
      description: ["description", "desc", "text", "body", "label"],
      extra: ["extra", "icon", "subtitle", "detail"],
    },
    sampleRows: [
      { type: "stat", title: "4,000+", description: "Courses Listed", extra: "Verified courses nationwide" },
      { type: "stat", title: "50", description: "States Covered", extra: "Every state and major metro area" },
      { type: "stat", title: "250K+", description: "Monthly Putters", extra: "Players discovering new courses" },
      { type: "stat", title: "4.8★", description: "Average Rating", extra: "Authentic visitor reviews" },
      { type: "value", title: "Verified & Accurate Listings", description: "Every course profile includes verified hours, phone numbers, and genuine visitor reviews.", extra: "Trophy" },
      { type: "value", title: "Comprehensive Geography", description: "From coastal resort greens in Florida to indoor climate-controlled courses in the Midwest.", extra: "Compass" },
      { type: "story", title: "Our Mission", description: "Mini Golf USA was born out of a simple passion: making it easy for families and travelers to discover incredible miniature golf experiences anywhere in America.", extra: "" },
    ],
    normalize(raw) {
      return {
        type: (raw.type || "stat").trim().toLowerCase(),
        title: (raw.title || "").trim(),
        description: (raw.description || "").trim(),
        extra: (raw.extra || "").trim(),
      };
    },
    validate(item) {
      const errors = [];
      if (!item.title) errors.push("Title / Number is required");
      if (!item.description) errors.push("Description is required");
      return errors;
    },
  },

  contact: {
    id: "contact",
    title: "Contact Us & FAQs",
    badge: "Contact",
    description: "Import Frequently Asked Questions (FAQs) and official contact channel properties.",
    filename: "contact_faqs_template.csv",
    columns: [
      { key: "type", label: "Type", required: true, description: "faq or info" },
      { key: "question_or_field", label: "Question or Field", required: true, description: "Question text or field key (email, phone, hours, address)" },
      { key: "answer_or_value", label: "Answer or Value", required: true, description: "Answer text or contact value" },
    ],
    aliases: {
      type: ["type", "category"],
      question_or_field: ["question_or_field", "question", "q", "field", "property"],
      answer_or_value: ["answer_or_value", "answer", "a", "value", "val"],
    },
    sampleRows: [
      { type: "faq", question_or_field: "How can I add or update my mini golf course listing?", answer_or_value: "You can reach out through our contact form with your venue name, address, and operating hours. Our editorial team will review and publish your listing." },
      { type: "faq", question_or_field: "Are the hours and ticket prices up to date?", answer_or_value: "We verify listings regularly, but we always suggest calling the venue or checking their official website for holiday schedules." },
      { type: "faq", question_or_field: "Can I submit photos or review corrections?", answer_or_value: "Yes! Send us an inquiry with the course name and updated photos or details." },
      { type: "info", question_or_field: "email", answer_or_value: "contact@minigolfusa.online" },
      { type: "info", question_or_field: "phone", answer_or_value: "(555) 382-4653" },
      { type: "info", question_or_field: "hours", answer_or_value: "Monday – Friday: 9:00 AM – 6:00 PM EST" },
    ],
    normalize(raw) {
      return {
        type: (raw.type || "faq").trim().toLowerCase(),
        question_or_field: (raw.question_or_field || raw.question || raw.field || "").trim(),
        answer_or_value: (raw.answer_or_value || raw.answer || raw.value || "").trim(),
      };
    },
    validate(item) {
      const errors = [];
      if (!item.question_or_field) errors.push("Question or Field is required");
      if (!item.answer_or_value) errors.push("Answer or Value is required");
      return errors;
    },
  },

  schema: {
    id: "schema",
    title: "Schema & SEO Settings",
    badge: "SEO",
    description: "Import website SEO meta tags, social links, verification tokens, and structured data switches.",
    filename: "seo_schema_settings_template.csv",
    columns: [
      { key: "setting", label: "Setting Key", required: true, description: "e.g. siteName, siteUrl, defaultTitle, defaultDescription, contactEmail" },
      { key: "value", label: "Setting Value", required: true, description: "Value or boolean true/false" },
    ],
    aliases: {
      setting: ["setting", "key", "property", "name"],
      value: ["value", "val", "text"],
    },
    sampleRows: [
      { setting: "siteName", value: "Mini Golf USA" },
      { setting: "siteUrl", value: "https://minigolfalabama.com" },
      { setting: "defaultTitle", value: "Mini Golf Alabama | Best Indoor & Outdoor Mini Golf Near Me" },
      { setting: "defaultDescription", value: "Find a fun and exciting mini golf putt putt course near me in Alabama and across the USA for family outings, date nights, and friendly competition." },
      { setting: "contactEmail", value: "contact@minigolfalabama.com" },
      { setting: "contactPhone", value: "(800) 555-GOLF" },
      { setting: "googleSiteVerification", value: "bi4Dgit8tyad33aIxnsWe3v0F_ufmeXoIziR7ybwYjU" },
      { setting: "enableWebsiteSchema", value: "true" },
      { setting: "enableOrganizationSchema", value: "true" },
      { setting: "enableCourseSchema", value: "true" },
      { setting: "enableFaqSchema", value: "true" },
      { setting: "enableBreadcrumbSchema", value: "true" },
      { setting: "enableCollectionSchema", value: "true" },
      { setting: "enableBlogSchema", value: "true" },
    ],
    normalize(raw) {
      return {
        setting: (raw.setting || raw.key || "").trim(),
        value: (raw.value || "").trim(),
      };
    },
    validate(item) {
      const errors = [];
      if (!item.setting) errors.push("Setting key is required");
      return errors;
    },
  },
};
